import { useEffect, useState } from 'react';
import { ApiError, call, tasteApi, themeApi, type MaxQuality, type Policy, type ThemeListItem } from '../api';
import { Avatar } from '../components/Avatar';
import { TasteProfile } from '../components/TasteProfile';
import { linkProps } from '../router';
import { ago, hours, tautulliApi } from '../tautulli';

interface Own { subtitles: string; audio: string; sourceOrder: string; addons: number; layout: boolean; navCustom: boolean; debrid?: string[] }
interface Row { uuid: string; title: string; thumb: string; restricted: boolean; admin: boolean; policy: Policy; custom: boolean; skin: string | null; now: string | null; own: Own }
interface AccountBlock { accountId: number; name: string; sub: string; role: 'owner' | 'household' | 'guest'; thumb: string; iptv: number; trakt: boolean; profiles: Row[] }
interface Template { id: string; name: string; policy: Policy }
interface Approval { id: string; username: string; profileTitle: string; mediaType: 'movie' | 'tv'; tmdbId: number; seasons: number[] | null; title: string | null; poster: string | null; createdAt: number }
interface Data { templates: Template[]; accounts: AccountBlock[]; stats: { accounts: number; profiles: number; watching: number; approvals: number } }

const QUALITY: [MaxQuality, string][] = [['original', 'Origineel'], [20000, '1080p · 20 Mbps'], [8000, '1080p · 8 Mbps'], [4000, '720p · 4 Mbps']];
const qualityLabel = (q: MaxQuality) => QUALITY.find(([x]) => x === q)?.[1] ?? 'Origineel';
const ROLE: Record<AccountBlock['role'], string> = { owner: 'Beheerder', household: 'Thuis', guest: 'Gast' };
const SOURCE: Record<string, string> = { 'plex-first': 'Eerst Plex', 'addons-first': 'Eerst addons', 'plex-only': 'Alleen Plex', 'addons-only': 'Alleen addons' };

const RIGHTS: { key: keyof Policy; label: string; hint: string }[] = [
  { key: 'liveTv', label: 'Live TV', hint: 'IPTV-zenders, sport en de gids' },
  { key: 'requests', label: 'Aanvragen via Seerr', hint: 'Films en series aanvragen' },
  { key: 'requestApproval', label: 'Aanvragen eerst goedkeuren', hint: 'Jij keurt een aanvraag goed voordat hij naar Seerr gaat' },
  { key: 'addons', label: 'Addons', hint: 'Eigen addons toevoegen (video en muziek)' },
  { key: 'music', label: 'Muziek', hint: 'Het muziekgedeelte' },
  { key: 'debrid', label: 'Debrid', hint: 'Eigen TorBox- of Real-Debrid-sleutel koppelen; afspelen gaat via de eigen internetverbinding (kost deze server niets)' },
  { key: 'usenet', label: 'Usenet', hint: 'Bronnen via jouw NzbDAV en indexers; afspelen gaat via deze server en jouw Usenet-abonnement' },
  { key: 'downloads', label: 'Offline downloaden', hint: 'Films en afleveringen op de iPhone/iPad zetten, altijd het originele bestand. Plex en Usenet gaan via deze server (alleen upload, geen omzetten); debrid rechtstreeks' },
  { key: 'layoutEdit', label: 'Startscherm aanpassen', hint: 'Eigen widgets en volgorde' },
  { key: 'skinLocked', label: 'Skin vastzetten', hint: 'Het profiel kan zelf geen andere skin kiezen' },
];

const onOff = (b: boolean) => (b ? 'Aan' : 'Uit');

/** Gebruikers en rechten (alleen de beheerder): iedereen die tumedia gebruikt, met per profiel wat mag. */
export function UsersAdmin() {
  const [data, setData] = useState<Data | null>(null);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [skins, setSkins] = useState<ThemeListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ row: Row; account: AccountBlock } | null>(null);
  const [watch, setWatch] = useState<Record<string, { month: number; total: number; plays: number; lastSeen: number | null }>>({});

  const load = () => {
    call<Data>('/api/admin/users')
      .then((d) => {
        setData(d);
        setOpen((o) => {
          if (!o) return o;
          for (const a of d.accounts) for (const p of a.profiles) if (p.uuid === o.row.uuid) return { row: p, account: a };
          return null;
        });
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.status === 403 ? 'Alleen de beheerder van de server kan gebruikers beheren.' : 'Gebruikers laden mislukt.'));
    call<{ approvals: Approval[] }>('/api/admin/approvals').then((r) => setApprovals(r.approvals)).catch(() => setApprovals([]));
  };

  useEffect(() => {
    load();
    tautulliApi.userStats().then((r) => setWatch(r.stats)).catch(() => setWatch({}));
    themeApi.list().then((r) => setSkins(r.themes.filter((t) => t.scope !== 'personal'))).catch(() => setSkins([]));
  }, []);

  const decide = async (a: Approval, approve: boolean) => {
    try {
      await call(`/api/admin/approvals/${a.id}`, { method: 'POST', body: JSON.stringify({ approve }) });
      setApprovals((l) => l.filter((x) => x.id !== a.id));
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Dat lukte niet.');
    }
  };

  if (open && data) {
    return <UserDetail row={open.row} account={open.account} templates={data.templates} skins={skins} onBack={() => setOpen(null)} onChanged={load} />;
  }

  const stats = data?.stats;
  return (
    <div className="ua2">
      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {!data && !error && <p className="placeholder-row">Laden…</p>}
      {stats && (
        <div className="ov-tiles">
          <div className="ov-tile"><span className="subtle">Accounts</span><strong>{stats.accounts}</strong></div>
          <div className="ov-tile"><span className="subtle">Profielen</span><strong>{stats.profiles}</strong></div>
          <div className="ov-tile"><span className="subtle">Kijkt nu</span><strong className={stats.watching ? 'is-accent' : undefined}>{stats.watching}</strong></div>
          <div className="ov-tile"><span className="subtle">Wacht op goedkeuring</span><strong className={approvals.length ? 'is-accent' : undefined}>{approvals.length}</strong></div>
        </div>
      )}

      {approvals.length > 0 && (
        <section className="ov-card" aria-label="Aanvragen die wachten op goedkeuring">
          <div className="ov-head"><h2>Wacht op goedkeuring</h2></div>
          <ul className="ua-approvals">
            {approvals.map((a) => (
              <li key={a.id} className="ua-approval">
                <div className="ua-ap-poster" style={a.poster ? { backgroundImage: `url("${a.poster}")` } : undefined} />
                <div className="ua-ap-body">
                  <strong>{a.title ?? `${a.mediaType === 'tv' ? 'Serie' : 'Film'} #${a.tmdbId}`}</strong>
                  <span className="subtle">
                    {a.mediaType === 'tv' ? `Serie${a.seasons?.length ? ` · seizoen ${a.seasons.join(', ')}` : ' · alle seizoenen'}` : 'Film'} · {a.profileTitle}
                    {a.username && a.username !== a.profileTitle ? ` (${a.username})` : ''} · {new Date(a.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <div className="form-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => void decide(a, true)}>Goedkeuren</button>
                  <button className="btn btn-sm" onClick={() => void decide(a, false)}>Afwijzen</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data?.accounts.map((acc) => (
        <section key={`${acc.accountId}:${acc.role}`} className="ov-card ua2-acc" aria-label={acc.name}>
          <div className="ua2-acc-head">
            {acc.thumb ? <Avatar src={acc.thumb} name={acc.name} className="ua2-acc-avatar" /> : <span className="ua2-acc-avatar ua2-acc-icon" aria-hidden="true">⌂</span>}
            <div className="ua2-acc-text">
              <strong>{acc.name}</strong>
              <span className="subtle">{acc.sub}</span>
            </div>
            <span className={`ua-role ua-role-${acc.role}`}>{ROLE[acc.role]}</span>
            <span className="subtle ua2-acc-own">
              {[acc.iptv ? `${acc.iptv} IPTV-account${acc.iptv === 1 ? '' : 's'}` : null, acc.trakt ? 'Trakt gekoppeld' : null].filter(Boolean).join(' · ') || 'Geen eigen koppelingen'}
            </span>
          </div>
          <div className="ua2-profiles">
            {acc.profiles.map((p) => {
              const tpl = data.templates.find((t) => t.id === p.policy.template)?.name ?? (p.custom ? 'Eigen' : '—');
              const w = watch[p.title.toLowerCase()];
              const chips: [string, boolean][] = [
                ['Live TV', p.policy.liveTv],
                [p.policy.requests && p.policy.requestApproval && !p.admin ? 'Aanvragen (goedkeuren)' : 'Aanvragen', p.policy.requests],
                [p.policy.addons && p.own.addons ? `Addons (${p.own.addons})` : 'Addons', p.policy.addons],
                ['Muziek', p.policy.music],
              ];
              return (
                <button key={p.uuid} type="button" className="ua2-profile" onClick={() => setOpen({ row: p, account: acc })} aria-label={`${p.title} openen`}>
                  <Avatar src={p.thumb} name={p.title} className="ua2-avatar" />
                  <span className="ua2-profile-main">
                    <span className="ua2-profile-name">{p.title}{p.admin ? <em> · beheerder</em> : null}<span className="ua2-tpl">{tpl}</span></span>
                    <span className="ua2-chips">
                      {chips.map(([l, on]) => <span key={l} className={`ua2-chip ${on ? 'is-on' : ''}`}>{l}</span>)}
                      <span className="ua2-chip">{qualityLabel(p.policy.maxQuality)}</span>
                      {p.policy.maxAge != null && <span className="ua2-chip">t/m {p.policy.maxAge} jaar</span>}
                    </span>
                  </span>
                  <span className="ua2-profile-side subtle">
                    {p.now ? <span className="ua2-now">{p.now}</span> : w?.lastSeen ? `laatst ${ago(w.lastSeen)}` : '—'}
                    {w ? <span>{hours(w.month)} deze maand</span> : null}
                  </span>
                  <span className="ua2-go" aria-hidden="true">›</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {data && (
        <p className="subtle hint">
          Je ziet per gebruiker wélke addons en accounts er zijn, maar nooit hun adressen, tokens of wachtwoorden. Die zijn alleen van henzelf.
          Vrienden verschijnen hier zodra ze een keer hebben ingelogd.
        </p>
      )}
    </div>
  );
}

function UserDetail({ row, account, templates, skins, onBack, onChanged }: { row: Row; account: AccountBlock; templates: Template[]; skins: ThemeListItem[]; onBack: () => void; onChanged: () => void }) {
  const [policy, setPolicy] = useState<Policy>(row.policy);
  const [skin, setSkin] = useState(row.skin ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (patch: Partial<Policy> & { skinId?: string }) => {
    setBusy(true);
    setMsg(null);
    try {
      const next = { ...policy, template: patch.template ?? 'eigen', ...patch };
      const res = await call<{ policy: Policy; skin: string | null }>(`/api/policies/${encodeURIComponent(row.uuid)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...next, restricted: row.restricted }),
      });
      setPolicy(res.policy);
      setSkin(res.skin ?? '');
      setMsg('✓ Opgeslagen');
      onChanged();
    } catch {
      setMsg('Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    await call(`/api/policies/${encodeURIComponent(row.uuid)}`, { method: 'DELETE' }).catch(() => undefined);
    setMsg('Terug naar het standaardsjabloon.');
    onChanged();
  };

  const own: [string, string][] = [
    ['Ondertitels', row.own.subtitles],
    ['Audiotaal', row.own.audio],
    ['Volgorde van bronnen', SOURCE[row.own.sourceOrder] ?? row.own.sourceOrder],
    ['Debrid', row.own.debrid?.length ? `${row.own.debrid.join(', ')} (sleutel verborgen)` : 'niet gekoppeld'],
    ['Addons', row.own.addons ? `${row.own.addons} (adressen verborgen)` : 'geen'],
    ['Menu', row.own.navCustom ? 'eigen volgorde' : 'standaard'],
    ['IPTV-accounts (account)', account.iptv ? String(account.iptv) : 'geen'],
    ['Trakt', account.trakt ? 'gekoppeld' : 'niet gekoppeld'],
  ];

  const role = account.role === 'guest' ? (account.accountId < 0 ? 'Eigen account' : 'Eigen Plex-account') : row.restricted ? 'Kinderprofiel' : row.admin ? 'Beheerder' : 'Plex Home';
  const sw = (key: keyof Policy, label: string, hint: string) => {
    const disabled = busy || (key === 'requests' && row.restricted) || (key === 'requestApproval' && !policy.requests);
    const id = `ua-sw-${key}`;
    return (
      <div key={key} className="ua2-row">
        <span className="ua2-row-text">
          <label htmlFor={id}>{label}</label>
          <span className="subtle">{hint}</span>
        </span>
        <input id={id} className="sw" type="checkbox" role="switch" checked={!!policy[key]} disabled={disabled} onChange={(e) => void save({ [key]: e.target.checked } as Partial<Policy>)} />
      </div>
    );
  };

  return (
    <div className="ua2 ua2-detail">
      <nav aria-label="Kruimelpad" className="ua2-crumb">
        <button type="button" className="ua2-crumb-back" onClick={onBack}>Gebruikers en rechten</button> › {row.title}
      </nav>

      <header className="ov-card ua2-head">
        <Avatar src={row.thumb} name={row.title} className="ua2-head-avatar" />
        <div className="ua2-head-text">
          <h3>{row.title}</h3>
          <span className="ua2-head-meta">
            <span className={`ua-role ua-role-${account.role}`}>{role}</span>
            <span className="subtle">{account.role === 'guest' ? `Account ${account.name}` : account.name}</span>
            {row.now && <span className="subtle">· nu: {row.now}</span>}
          </span>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-sm" onClick={() => void reset()} disabled={busy || !row.custom}>Terug naar sjabloon</button>
        </div>
      </header>
      {msg && <p className="subtle ua2-msg" role="status">{msg}</p>}

      <div className="ov-row">
        <div className="ua2-main">
          <section className="ov-card" aria-label="Sjabloon">
            <div className="ov-head"><h2>Sjabloon</h2><span className="subtle">Begin bij een sjabloon en pas daarna aan</span></div>
            <div className="chip-wrap">
              {templates.map((t) => (
                <button key={t.id} type="button" className="chip" aria-pressed={policy.template === t.id} disabled={busy} onClick={() => void save({ template: t.id })}>{t.name}</button>
              ))}
              {(!policy.template || policy.template === 'eigen') && <span className="chip" aria-pressed="true">Eigen</span>}
            </div>
          </section>

          <section className="ov-card" aria-label="Toegang">
            <div className="ov-head"><h2>Toegang</h2></div>
            <div>{RIGHTS.map((r) => sw(r.key, r.label, r.hint))}</div>
          </section>

          <section className="ov-card" aria-label="Kwaliteit en leeftijd">
            <div className="ov-head"><h2>Kwaliteit, leeftijd en skin</h2></div>
            <div className="ua2-selects">
              <label className="ua2-select">Hoogste kwaliteit (Plex)
                <select value={String(policy.maxQuality)} disabled={busy} onChange={(e) => void save({ maxQuality: (e.target.value === 'original' ? 'original' : Number(e.target.value)) as MaxQuality })}>
                  {QUALITY.map(([q, l]) => <option key={String(q)} value={String(q)}>{l}</option>)}
                </select>
              </label>
              <label className="ua2-select">Leeftijdsgrens
                <select value={policy.maxAge == null ? '' : String(policy.maxAge)} disabled={busy} onChange={(e) => void save({ maxAge: e.target.value ? Number(e.target.value) : null } as Partial<Policy>)}>
                  <option value="">Geen grens</option>
                  {[6, 9, 12, 14, 16, 18].map((a) => <option key={a} value={a}>t/m {a} jaar</option>)}
                </select>
              </label>
              <label className="ua2-select">Skin
                <select value={skin} disabled={busy} onChange={(e) => void save({ skinId: e.target.value })}>
                  <option value="">Zelf kiezen</option>
                  {skins.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </label>
            </div>
            <p className="subtle hint">De leeftijdsgrens geldt overal (Home, bibliotheek, zoeken, collecties, kalender, aanbevelingen en aanvragen; web en apps). Titels zonder bekende keuring worden dan niet getoond.</p>
          </section>
        </div>

        <div className="ua2-side">
        <section className="ov-card" aria-label="Smaakprofiel">
          <div className="ov-head"><h2>Smaakprofiel</h2></div>
          <TasteProfile load={() => tasteApi.of(row.uuid, row.title, row.admin)} />
        </section>
        <aside className="ov-card ua2-aside" aria-label="Eigen instellingen">
          <div className="ov-head"><h2>Eigen instellingen</h2></div>
          <span className="subtle">Stelt het profiel zelf in. Jij kunt het alleen zien; geheime gegevens blijven verborgen.</span>
          <dl className="ua2-own">
            {own.map(([k, v]) => (
              <div key={k}><dt className="subtle">{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
          {row.restricted && (
            <p className="subtle hint">Bedtijd en de ouder-PIN stel je in bij <a {...linkProps('/settings/household')}>Gezin en profielen</a>.</p>
          )}
        </aside>
        </div>
      </div>
    </div>
  );
}
